import React, { createContext, useContext, useState, useEffect } from 'react';

// Comprehensive country data from the original website
const countryData = {
    en: {
        italy: {
            name: "Italy",
            code: "IT",
            overview: "Italy offers excellent access to the EU market with a strong legal framework and established business environment. Perfect for companies looking to expand into Europe.",
            advantages: [
                "Full EU market access",
                "Strong legal and regulatory framework",
                "Strategic Mediterranean location",
                "Well-developed infrastructure",
                "Access to skilled workforce"
            ],
            requirements: [
                "Minimum share capital: €1",
                "At least one director required",
                "Registered office in Italy",
                "Company secretary not mandatory",
                "Annual filing requirements"
            ],
            timeframe: "2-4 weeks average formation time",
            taxation: "Corporate tax rate: 24% + regional tax (up to 5.57%)"
        },
        slovenia: {
            name: "Slovenia",
            code: "SI",
            overview: "Slovenia provides an excellent gateway to EU markets with business-friendly regulations and competitive operational costs.",
            advantages: [
                "EU member state benefits",
                "Business-friendly environment",
                "Low operational costs",
                "Strategic Central European location",
                "Stable political climate"
            ],
            requirements: [
                "Minimum share capital: €7,500",
                "At least one director required",
                "Registered office in Slovenia",
                "Supervisory board for larger companies",
                "Annual reporting obligations"
            ],
            timeframe: "2-3 weeks average formation time",
            taxation: "Corporate tax rate: 19%"
        },
        macedonia: {
            name: "North Macedonia",
            code: "MK",
            overview: "North Macedonia offers a growing economy with low corporate tax rates and simplified company registration procedures.",
            advantages: [
                "Low corporate tax rate (10%)",
                "Growing economy",
                "EU candidate status",
                "Simple registration process",
                "Strategic Balkan location"
            ],
            requirements: [
                "Minimum share capital: €2,500",
                "At least one director required",
                "Registered office in Macedonia",
                "Annual financial statements",
                "VAT registration if applicable"
            ],
            timeframe: "1-2 weeks average formation time",
            taxation: "Corporate tax rate: 10%"
        },
        serbia: {
            name: "Serbia",
            code: "RS",
            overview: "Serbia offers strategic location advantages with competitive costs and improving business environment for international companies.",
            advantages: [
                "Strategic Balkan location",
                "Competitive operational costs",
                "Growing economy",
                "Skilled workforce",
                "EU accession negotiations"
            ],
            requirements: [
                "Minimum share capital: €1",
                "At least one director required",
                "Registered office in Serbia",
                "Business registration certificate",
                "Tax registration required"
            ],
            timeframe: "1-3 weeks average formation time",
            taxation: "Corporate tax rate: 15%"
        },
        bosnia: {
            name: "Bosnia & Herzegovina",
            code: "BA",
            overview: "Bosnia & Herzegovina provides emerging market opportunities with strategic location and competitive business costs.",
            advantages: [
                "Strategic Balkan location",
                "Low operational costs",
                "Emerging market opportunities",
                "Growing service sector",
                "EU association agreement"
            ],
            requirements: [
                "Minimum share capital: €1",
                "At least one founder required",
                "Registered office in B&H",
                "Court registration",
                "Tax and social security registration"
            ],
            timeframe: "2-4 weeks average formation time",
            taxation: "Corporate tax rate: 10%"
        },
        bulgaria: {
            name: "Bulgaria",
            code: "BG",
            overview: "Bulgaria offers EU market access with one of the lowest corporate tax rates in Europe and competitive business costs.",
            advantages: [
                "EU member state",
                "Lowest corporate tax in EU (10%)",
                "Strategic Black Sea location",
                "Skilled workforce",
                "Growing IT sector"
            ],
            requirements: [
                "Minimum share capital: €2",
                "At least one director required",
                "Registered office in Bulgaria",
                "Commercial register entry",
                "Annual accounting obligations"
            ],
            timeframe: "1-2 weeks average formation time",
            taxation: "Corporate tax rate: 10%"
        },
        croatia: {
            name: "Croatia",
            code: "HR",
            overview: "Croatia combines EU membership with a beautiful Adriatic location, making it ideal for tourism, tech, and service businesses.",
            advantages: [
                "EU member state",
                "Tourism and hospitality hub",
                "Growing tech sector",
                "Strategic Adriatic location",
                "Stable political environment"
            ],
            requirements: [
                "Minimum share capital: €2,500",
                "At least one director required",
                "Registered office in Croatia",
                "Court registration",
                "Annual financial reporting"
            ],
            timeframe: "2-3 weeks average formation time",
            taxation: "Corporate tax rate: 18%"
        },
        usa: {
            name: "United States",
            code: "US",
            overview: "The United States offers access to the world's largest economy with flexible business structures, particularly in Delaware and Wyoming.",
            advantages: [
                "World's largest economy",
                "Flexible business structures",
                "Delaware/Wyoming advantages",
                "Strong legal system",
                "Access to global markets"
            ],
            requirements: [
                "State-specific requirements",
                "Registered agent required",
                "Operating agreement recommended",
                "EIN tax identification",
                "State filing fees"
            ],
            timeframe: "1-2 weeks average formation time",
            taxation: "Federal corporate rate: 21% + state taxes"
        },
        uk: {
            name: "United Kingdom",
            code: "GB",
            overview: "The UK remains a global financial center with strong international reputation and well-established business infrastructure.",
            advantages: [
                "Global financial center",
                "Strong international reputation",
                "English common law system",
                "Time zone advantages",
                "Established banking system"
            ],
            requirements: [
                "Minimum share capital: £100",
                "At least one director required",
                "Registered office in UK",
                "Companies House filing",
                "Annual confirmation statement"
            ],
            timeframe: "1-2 weeks average formation time",
            taxation: "Corporate tax rate: 25% (19% for small companies)"
        },
        greece: {
            name: "Greece",
            code: "GR",
            overview: "Greece offers EU membership benefits with strategic Mediterranean location and access to Balkan and Middle Eastern markets.",
            advantages: [
                "EU member state",
                "Strategic Mediterranean location",
                "Shipping industry hub",
                "Tourism opportunities",
                "Cultural heritage"
            ],
            requirements: [
                "Minimum share capital: €24,000",
                "At least one director required",
                "Registered office in Greece",
                "General Commercial Registry",
                "Annual financial statements"
            ],
            timeframe: "3-4 weeks average formation time",
            taxation: "Corporate tax rate: 22%"
        },
        uae: {
            name: "UAE (Dubai)",
            code: "AE",
            overview: "The UAE, particularly Dubai, offers tax-free zones and serves as a gateway to Middle Eastern and African markets.",
            advantages: [
                "0% corporate tax in free zones",
                "Gateway to Middle East & Africa",
                "World-class infrastructure",
                "Business-friendly environment",
                "Strategic location"
            ],
            requirements: [
                "Free zone or mainland options",
                "Local service agent (mainland)",
                "Minimum share capital varies",
                "UAE resident director option",
                "Commercial license required"
            ],
            timeframe: "2-4 weeks average formation time",
            taxation: "0% corporate tax in free zones"
        },
        singapore: {
            name: "Singapore",
            code: "SG",
            overview: "Singapore is the premier Asia-Pacific business hub with excellent infrastructure, political stability, and strategic location.",
            advantages: [
                "Asia-Pacific business hub",
                "Political stability",
                "Excellent infrastructure",
                "Strategic location",
                "Strong legal system"
            ],
            requirements: [
                "Minimum share capital: S$1",
                "At least one local director",
                "Registered office in Singapore",
                "Company secretary required",
                "Annual filing requirements"
            ],
            timeframe: "1-2 weeks average formation time",
            taxation: "Corporate tax rate: 17% (with exemptions)"
        },
        cyprus: {
            name: "Cyprus",
            code: "CY",
            overview: "Cyprus offers EU membership benefits with an attractive tax regime and strategic location between Europe, Asia, and Africa.",
            advantages: [
                "EU member state",
                "Attractive tax regime (12.5% corporate tax)",
                "Strategic geographical location",
                "International business center",
                "Double tax treaty network"
            ],
            requirements: [
                "Minimum share capital: €1,000",
                "At least one director required",
                "Registered office in Cyprus",
                "Company secretary required",
                "Annual filing obligations"
            ],
            timeframe: "2-3 weeks average formation time",
            taxation: "Corporate tax rate: 12.5%"
        },
        montenegro: {
            name: "Montenegro",
            code: "ME",
            overview: "Montenegro offers emerging market opportunities with EU candidate status and competitive business environment.",
            advantages: [
                "EU candidate status",
                "Growing economy",
                "Low operational costs",
                "Strategic Adriatic location",
                "Business-friendly reforms"
            ],
            requirements: [
                "Minimum share capital: €1",
                "At least one founder required",
                "Registered office in Montenegro",
                "Court registration",
                "Tax registration required"
            ],
            timeframe: "2-4 weeks average formation time",
            taxation: "Corporate tax rate: 9%"
        },
        worldwide: {
            name: "Rest of the World",
            code: "WW",
            overview: "We provide company formation services in many additional jurisdictions worldwide. Each jurisdiction has unique advantages and requirements tailored to specific business needs.",
            advantages: [
                "Global jurisdiction coverage",
                "Customized solutions",
                "Expert local knowledge",
                "Compliance management",
                "Ongoing support services"
            ],
            requirements: [
                "Requirements vary by jurisdiction",
                "Detailed consultation required",
                "Custom documentation needed",
                "Local regulatory compliance",
                "Higher service fees apply"
            ],
            timeframe: "Varies by jurisdiction (typically 2-8 weeks)",
            taxation: "Tax rates vary significantly by jurisdiction",
            special: "Available on request with premium pricing - Contact us for detailed consultation and custom quote"
        }
    },
    
    de: {
        italy: {
            name: "Italien",
            code: "IT",
            overview: "Italien bietet ausgezeichneten Zugang zum EU-Markt mit einem starken Rechtsrahmen und etabliertem Geschäftsumfeld. Perfekt für Unternehmen, die nach Europa expandieren möchten.",
            advantages: [
                "Vollständiger EU-Marktzugang",
                "Starker Rechts- und Regulierungsrahmen",
                "Strategische Lage im Mittelmeerraum",
                "Gut entwickelte Infrastruktur",
                "Zugang zu qualifizierten Arbeitskräften"
            ],
            requirements: [
                "Mindestgrundkapital: €1",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Italien",
                "Unternehmenssekretär nicht obligatorisch",
                "Jährliche Einreichungspflichten"
            ],
            timeframe: "2-4 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 24% + regionale Steuer (bis zu 5,57%)"
        },
        slovenia: {
            name: "Slowenien",
            code: "SI",
            overview: "Slowenien bietet einen ausgezeichneten Zugang zu EU-Märkten mit unternehmensfreundlichen Regelungen und wettbewerbsfähigen Betriebskosten.",
            advantages: [
                "EU-Mitgliedsstaat-Vorteile",
                "Unternehmensfreundliches Umfeld",
                "Niedrige Betriebskosten",
                "Strategische zentraleuropäische Lage",
                "Stabiles politisches Klima"
            ],
            requirements: [
                "Mindestgrundkapital: €7.500",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Slowenien",
                "Aufsichtsrat für größere Unternehmen",
                "Jährliche Berichtspflichten"
            ],
            timeframe: "2-3 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 19%"
        },
        macedonia: {
            name: "Nordmazedonien",
            code: "MK",
            overview: "Nordmazedonien bietet eine wachsende Wirtschaft mit niedrigen Körperschaftssteuersätzen und vereinfachten Unternehmensregistrierungsverfahren.",
            advantages: [
                "Niedriger Körperschaftssteuersatz (10%)",
                "Wachsende Wirtschaft",
                "EU-Kandidatenstatus",
                "Einfacher Registrierungsprozess",
                "Strategische Balkan-Lage"
            ],
            requirements: [
                "Mindestgrundkapital: €2.500",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Mazedonien",
                "Jährliche Finanzberichte",
                "MwSt-Registrierung falls zutreffend"
            ],
            timeframe: "1-2 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 10%"
        },
        serbia: {
            name: "Serbien",
            code: "RS",
            overview: "Serbien bietet strategische Standortvorteile mit wettbewerbsfähigen Kosten und verbessertem Geschäftsumfeld für internationale Unternehmen.",
            advantages: [
                "Strategische Balkan-Lage",
                "Wettbewerbsfähige Betriebskosten",
                "Wachsende Wirtschaft",
                "Qualifizierte Arbeitskräfte",
                "EU-Beitrittsverhandlungen"
            ],
            requirements: [
                "Mindestgrundkapital: €1",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Serbien",
                "Gewerbeanmeldung",
                "Steuerliche Registrierung erforderlich"
            ],
            timeframe: "1-3 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 15%"
        },
        bosnia: {
            name: "Bosnien & Herzegowina",
            code: "BA",
            overview: "Bosnien & Herzegowina bietet Möglichkeiten in aufstrebenden Märkten mit strategischer Lage und wettbewerbsfähigen Geschäftskosten.",
            advantages: [
                "Strategische Balkan-Lage",
                "Niedrige Betriebskosten",
                "Möglichkeiten in aufstrebenden Märkten",
                "Wachsender Dienstleistungssektor",
                "EU-Assoziierungsabkommen"
            ],
            requirements: [
                "Mindestgrundkapital: €2.500",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Bosnien",
                "Gewerbliche Genehmigungen",
                "Steuerliche Registrierung"
            ],
            timeframe: "2-4 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 10%"
        },
        romania: {
            name: "Rumänien",
            code: "RO",
            overview: "Rumänien bietet EU-Marktzugang mit kostengünstigen Abläufen und wachsenden Geschäftsmöglichkeiten in Osteuropa.",
            advantages: [
                "EU-Mitgliedsstaat",
                "Kostengünstige Abläufe",
                "Wachsende Wirtschaft",
                "Strategische osteuropäische Lage",
                "Qualifizierte IT-Arbeitskräfte"
            ],
            requirements: [
                "Mindestgrundkapital: €200",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Rumänien",
                "Handelskammerregistrierung",
                "Steuerliche Verpflichtungen"
            ],
            timeframe: "2-3 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 16%"
        },
        slovakia: {
            name: "Slowakei",
            code: "SK",
            overview: "Die Slowakei bietet EU-Zugang mit einer entwickelten Fertigungsbasis und wettbewerbsfähigen Kosten in Mitteleuropa.",
            advantages: [
                "EU-Mitgliedsstaat und Eurozone",
                "Entwickelte Fertigungsbasis",
                "Wettbewerbsfähige Arbeitskosten",
                "Zentrale europäische Lage",
                "Qualifizierte Arbeitskräfte"
            ],
            requirements: [
                "Mindestgrundkapital: €5.000",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in der Slowakei",
                "Notarielle Beglaubigung",
                "Handelsregisteranmeldung"
            ],
            timeframe: "2-4 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 21%"
        },
        estonia: {
            name: "Estland",
            code: "EE",
            overview: "Estland bietet fortschrittliche digitale Infrastruktur mit EU-Zugang und innovativen E-Residency-Programmen für internationale Unternehmer.",
            advantages: [
                "Fortschrittliche digitale Infrastruktur",
                "E-Residency-Programm",
                "EU-Mitgliedsstaat",
                "Innovative Tech-Umgebung",
                "Einfache Online-Verwaltung"
            ],
            requirements: [
                "Mindestgrundkapital: €2.500",
                "Mindestens ein Geschäftsführer erforderlich",
                "Virtuelle oder physische Adresse",
                "E-Residency möglich",
                "Online-Registrierung verfügbar"
            ],
            timeframe: "1-2 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 20% (auf ausgeschüttete Gewinne)"
        },
        uae: {
            name: "VAE (Dubai)",
            code: "AE",
            overview: "Die VAE bieten steuerfreie Zonen und fungieren als Tor zum Nahen Osten mit erstklassiger Infrastruktur und Geschäftsmöglichkeiten.",
            advantages: [
                "Steuerfreie Zonen verfügbar",
                "Tor zum Nahen Osten",
                "Erstklassige Infrastruktur",
                "100% Ausländereigentum möglich",
                "Strategische globale Lage"
            ],
            requirements: [
                "Mindestgrundkapital: Variiert je nach Zone",
                "Mindestens ein Geschäftsführer erforderlich",
                "Bürofläche in den VAE",
                "Lokaler Sponsor (für Festland)",
                "Handelslizenzen erforderlich"
            ],
            timeframe: "2-4 Wochen durchschnittliche Gründungszeit",
            taxation: "0% Körperschaftssteuer in Freizonen"
        },
        singapore: {
            name: "Singapur",
            code: "SG",
            overview: "Singapur ist ein führendes asiatisch-pazifisches Zentrum mit exzellenter Infrastruktur und einem unternehmensfreundlichen Regulierungsumfeld.",
            advantages: [
                "Asiatisch-pazifisches Geschäftszentrum",
                "Exzellente Infrastruktur",
                "Politische Stabilität",
                "Qualifizierte Arbeitskräfte",
                "Strategische asiatische Lage"
            ],
            requirements: [
                "Mindestgrundkapital: S$1",
                "Mindestens ein lokaler Geschäftsführer",
                "Registrierter Firmensitz in Singapur",
                "Unternehmenssekretär erforderlich",
                "Jährliche Einreichungen"
            ],
            timeframe: "1-2 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 17%"
        },
        cyprus: {
            name: "Zypern",
            code: "CY",
            overview: "Zypern bietet EU-Mitgliedschaft mit attraktivem Steuerregime und strategischer Lage zwischen Europa, Asien und Afrika.",
            advantages: [
                "EU-Mitgliedsstaat",
                "Attraktives Steuerregime",
                "Strategische Lage",
                "Englisches Rechtssystem",
                "Entwickelter Finanzsektor"
            ],
            requirements: [
                "Mindestgrundkapital: €1.000",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Zypern",
                "Unternehmenssekretär erforderlich",
                "Jährliche Berichterstattung"
            ],
            timeframe: "2-3 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 12,5%"
        },
        montenegro: {
            name: "Montenegro",
            code: "ME",
            overview: "Montenegro bietet EU-Kandidatenstatus mit aufstrebenden Geschäftsmöglichkeiten und attraktiven Investitionsanreizen.",
            advantages: [
                "EU-Kandidatenstatus",
                "Aufstrebende Geschäftsmöglichkeiten",
                "Niedrige Körperschaftssteuer",
                "Tourismus und Immobilienwachstum",
                "Strategische Balkanlage"
            ],
            requirements: [
                "Mindestgrundkapital: €1",
                "Mindestens ein Geschäftsführer erforderlich",
                "Registrierter Firmensitz in Montenegro",
                "Gerichtliche Registrierung",
                "Steuerliche Anmeldung"
            ],
            timeframe: "2-4 Wochen durchschnittliche Gründungszeit",
            taxation: "Körperschaftssteuersatz: 9%"
        },
        worldwide: {
            name: "Rest der Welt",
            code: "WW",
            overview: "Wir bieten Unternehmensregistrierungsdienste in vielen zusätzlichen Jurisdiktionen weltweit. Jede Jurisdiktion hat einzigartige Vorteile und Anforderungen, die auf spezifische Geschäftsbedürfnisse zugeschnitten sind.",
            advantages: [
                "Globale Jurisdiktionsabdeckung",
                "Maßgeschneiderte Lösungen",
                "Expertenwissen vor Ort",
                "Compliance-Management",
                "Laufende Unterstützungsdienste"
            ],
            requirements: [
                "Anforderungen variieren je nach Jurisdiktion",
                "Detaillierte Beratung erforderlich",
                "Individuelle Dokumentation notwendig",
                "Lokale regulatorische Compliance",
                "Höhere Servicegebühren fallen an"
            ],
            timeframe: "Variiert je nach Jurisdiktion (typisch 2-8 Wochen)",
            taxation: "Steuersätze variieren erheblich je nach Jurisdiktion",
            special: "Auf Anfrage mit Premium-Preisen verfügbar - Kontaktieren Sie uns für detaillierte Beratung und individuelles Angebot"
        }
    },
    
    ru: {
        italy: {
            name: "Италия",
            code: "IT",
            overview: "Италия предлагает отличный доступ к рынку ЕС с сильной правовой базой и устоявшейся деловой средой. Идеально подходит для компаний, желающих расширяться в Европу.",
            advantages: [
                "Полный доступ к рынку ЕС",
                "Сильная правовая и регулятивная база",
                "Стратегическое средиземноморское расположение",
                "Хорошо развитая инфраструктура",
                "Доступ к квалифицированной рабочей силе"
            ],
            requirements: [
                "Минимальный уставный капитал: €1",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Италии",
                "Корпоративный секретарь не обязателен",
                "Требования к ежегодной отчетности"
            ],
            timeframe: "2-4 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 24% + региональный налог (до 5,57%)"
        },
        slovenia: {
            name: "Словения",
            code: "SI",
            overview: "Словения обеспечивает отличный доступ к рынкам ЕС с благоприятными для бизнеса регулированиями и конкурентоспособными операционными расходами.",
            advantages: [
                "Преимущества государства-члена ЕС",
                "Благоприятная для бизнеса среда",
                "Низкие операционные расходы",
                "Стратегическое центральноевропейское расположение",
                "Стабильный политический климат"
            ],
            requirements: [
                "Минимальный уставный капитал: €7,500",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Словении",
                "Наблюдательный совет для крупных компаний",
                "Обязательства по ежегодной отчетности"
            ],
            timeframe: "2-3 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 19%"
        },
        macedonia: {
            name: "Северная Македония",
            code: "MK",
            overview: "Северная Македония предлагает растущую экономику с низкими ставками корпоративного налога и упрощенными процедурами регистрации компаний.",
            advantages: [
                "Низкая ставка корпоративного налога (10%)",
                "Растущая экономика",
                "Статус кандидата в ЕС",
                "Простой процесс регистрации",
                "Стратегическое балканское расположение"
            ],
            requirements: [
                "Минимальный уставный капитал: €2,500",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Македонии",
                "Ежегодная финансовая отчетность",
                "Регистрация НДС при применимости"
            ],
            timeframe: "1-2 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 10%"
        },
        serbia: {
            name: "Сербия",
            code: "RS",
            overview: "Сербия предлагает преимущества стратегического расположения с конкурентоспособными затратами и улучшающейся деловой средой для международных компаний.",
            advantages: [
                "Стратегическое балканское расположение",
                "Конкурентоспособные операционные расходы",
                "Растущая экономика",
                "Квалифицированная рабочая сила",
                "Переговоры о вступлении в ЕС"
            ],
            requirements: [
                "Минимальный уставный капитал: €1",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Сербии",
                "Сертификат о регистрации бизнеса",
                "Требуется налоговая регистрация"
            ],
            timeframe: "1-3 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 15%"
        },
        bosnia: {
            name: "Босния и Герцеговина",
            code: "BA",
            overview: "Босния и Герцеговина предоставляет возможности развивающегося рынка со стратегическим расположением и конкурентоспособными бизнес-затратами.",
            advantages: [
                "Стратегическое балканское расположение",
                "Низкие операционные расходы",
                "Возможности развивающегося рынка",
                "Растущий сектор услуг",
                "Соглашение об ассоциации с ЕС"
            ],
            requirements: [
                "Минимальный уставный капитал: €2,500",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Боснии",
                "Коммерческие лицензии",
                "Налоговая регистрация"
            ],
            timeframe: "2-4 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 10%"
        },
        romania: {
            name: "Румыния",
            code: "RO",
            overview: "Румыния предлагает доступ к рынку ЕС с экономически эффективными операциями и растущими деловыми возможностями в Восточной Европе.",
            advantages: [
                "Государство-член ЕС",
                "Экономически эффективные операции",
                "Растущая экономика",
                "Стратегическое восточноевропейское расположение",
                "Квалифицированные IT-кадры"
            ],
            requirements: [
                "Минимальный уставный капитал: €200",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Румынии",
                "Регистрация в торговой палате",
                "Налоговые обязательства"
            ],
            timeframe: "2-3 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 16%"
        },
        slovakia: {
            name: "Словакия",
            code: "SK",
            overview: "Словакия предлагает доступ к ЕС с развитой производственной базой и конкурентоспособными затратами в Центральной Европе.",
            advantages: [
                "Государство-член ЕС и еврозоны",
                "Развитая производственная база",
                "Конкурентоспособные трудовые затраты",
                "Центральное европейское расположение",
                "Квалифицированная рабочая сила"
            ],
            requirements: [
                "Минимальный уставный капитал: €5,000",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Словакии",
                "Нотариальное заверение",
                "Регистрация в торговом реестре"
            ],
            timeframe: "2-4 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 21%"
        },
        estonia: {
            name: "Эстония",
            code: "EE",
            overview: "Эстония предлагает передовую цифровую инфраструктуру с доступом к ЕС и инновационными программами электронного резидентства для международных предпринимателей.",
            advantages: [
                "Передовая цифровая инфраструктура",
                "Программа электронного резидентства",
                "Государство-член ЕС",
                "Инновационная технологическая среда",
                "Простое онлайн-управление"
            ],
            requirements: [
                "Минимальный уставный капитал: €2,500",
                "Требуется минимум один директор",
                "Виртуальный или физический адрес",
                "Возможно электронное резидентство",
                "Доступна онлайн-регистрация"
            ],
            timeframe: "1-2 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 20% (на распределенную прибыль)"
        },
        uae: {
            name: "ОАЭ (Дубай)",
            code: "AE",
            overview: "ОАЭ предлагают безналоговые зоны и служат воротами на Ближний Восток с первоклассной инфраструктурой и деловыми возможностями.",
            advantages: [
                "Доступны безналоговые зоны",
                "Ворота на Ближний Восток",
                "Первоклассная инфраструктура",
                "Возможна 100% иностранная собственность",
                "Стратегическое глобальное расположение"
            ],
            requirements: [
                "Минимальный уставный капитал: Варьируется по зонам",
                "Требуется минимум один директор",
                "Офисное пространство в ОАЭ",
                "Местный спонсор (для материка)",
                "Требуются торговые лицензии"
            ],
            timeframe: "2-4 недели среднее время регистрации",
            taxation: "0% корпоративный налог в свободных зонах"
        },
        singapore: {
            name: "Сингапур",
            code: "SG",
            overview: "Сингапур является ведущим азиатско-тихоокеанским центром с отличной инфраструктурой и благоприятной для бизнеса регулятивной средой.",
            advantages: [
                "Азиатско-тихоокеанский бизнес-центр",
                "Отличная инфраструктура",
                "Политическая стабильность",
                "Квалифицированная рабочая сила",
                "Стратегическое азиатское расположение"
            ],
            requirements: [
                "Минимальный уставный капитал: S$1",
                "Требуется минимум один местный директор",
                "Зарегистрированный офис в Сингапуре",
                "Требуется корпоративный секретарь",
                "Ежегодные подачи"
            ],
            timeframe: "1-2 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 17%"
        },
        cyprus: {
            name: "Кипр",
            code: "CY",
            overview: "Кипр предлагает членство в ЕС с привлекательным налоговым режимом и стратегическим расположением между Европой, Азией и Африкой.",
            advantages: [
                "Государство-член ЕС",
                "Привлекательный налоговый режим",
                "Стратегическое расположение",
                "Английская правовая система",
                "Развитый финансовый сектор"
            ],
            requirements: [
                "Минимальный уставный капитал: €1,000",
                "Требуется минимум один директор",
                "Зарегистрированный офис на Кипре",
                "Требуется корпоративный секретарь",
                "Ежегодная отчетность"
            ],
            timeframe: "2-3 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 12,5%"
        },
        montenegro: {
            name: "Черногория",
            code: "ME",
            overview: "Черногория предлагает статус кандидата в ЕС с развивающимися деловыми возможностями и привлекательными инвестиционными стимулами.",
            advantages: [
                "Статус кандидата в ЕС",
                "Развивающиеся деловые возможности",
                "Низкий корпоративный налог",
                "Рост туризма и недвижимости",
                "Стратегическое балканское расположение"
            ],
            requirements: [
                "Минимальный уставный капитал: €1",
                "Требуется минимум один директор",
                "Зарегистрированный офис в Черногории",
                "Судебная регистрация",
                "Налоговая регистрация"
            ],
            timeframe: "2-4 недели среднее время регистрации",
            taxation: "Ставка корпоративного налога: 9%"
        },
        worldwide: {
            name: "Остальной мир",
            code: "WW",
            overview: "Мы предоставляем услуги регистрации компаний во многих дополнительных юрисдикциях по всему миру. Каждая юрисдикция имеет уникальные преимущества и требования, адаптированные к конкретным бизнес-потребностям.",
            advantages: [
                "Глобальное покрытие юрисдикций",
                "Индивидуальные решения",
                "Экспертные местные знания",
                "Управление соответствием",
                "Постоянные услуги поддержки"
            ],
            requirements: [
                "Требования варьируются по юрисдикциям",
                "Требуется подробная консультация",
                "Необходима индивидуальная документация",
                "Соблюдение местных регулятивных требований",
                "Применяются более высокие сервисные сборы"
            ],
            timeframe: "Варьируется по юрисдикциям (обычно 2-8 недель)",
            taxation: "Налоговые ставки значительно варьируются по юрисдикциям",
            special: "Доступно по запросу с премиальным ценообразованием - Свяжитесь с нами для подробной консультации и индивидуального предложения"
        }
    },
    
    sr: {
        slovenia: {
            name: "Словенија",
            code: "SI",
            overview: "Словенија пружа одличан приступ ЕУ тржиштима са пословно пријатељским регулативама и конкурентним оперативним трошковима.",
            advantages: [
                "Предности државе чланице ЕУ",
                "Пословно пријатељско окружење",
                "Ниски оперативни трошкови",
                "Стратешка централноевропска локација",
                "Стабилна политичка клима"
            ],
            requirements: [
                "Минимални основни капитал: €7.500",
                "Потребан је најмање један директор",
                "Регистрована канцеларија у Словенији",
                "Надзорни одбор за веће компаније",
                "Обавезе годишњег извештавања"
            ],
            timeframe: "2-3 недеље просечно време формирања",
            taxation: "Стопа корпоративног пореза: 19%"
        },
        serbia: {
            name: "Србија",
            code: "RS",
            overview: "Србија нуди стратешке локацијске предности са конкурентним трошковима и побољшањем пословног окружења за међународне компаније.",
            advantages: [
                "Стратешка балканска локација",
                "Конкурентни оперативни трошкови",
                "Растућа економија",
                "Квалификована радна снага",
                "Преговори о приступању ЕУ"
            ],
            requirements: [
                "Минимални основни капитал: €1",
                "Потребан је најмање један директор",
                "Регистрована канцеларија у Србији",
                "Сертификат о регистрацији пословања",
                "Потребна је пореска регистрација"
            ],
            timeframe: "1-3 недеље просечно време формирања",
            taxation: "Стопа корпоративног пореза: 15%"
        },
        worldwide: {
            name: "Остатак света",
            code: "WW",
            overview: "Пружамо услуге формирања компанија у многим додатним јурисдикцијама широм света. Свака јурисдикција има јединствене предности и захтеве прилагођене специфичним пословним потребама.",
            advantages: [
                "Глобално покривање јурисдикција",
                "Прилагођена решења",
                "Стручно локално знање",
                "Управљање усклађеношћу",
                "Сталне услуге подршке"
            ],
            requirements: [
                "Захтеви се разликују по јурисдикцијама",
                "Потребна је детаљна консултација",
                "Потребна је прилагођена документација",
                "Усклађеност са локалним прописима",
                "Примењују се виши сервисни трошкови"
            ],
            timeframe: "Варира по јурисдикцијама (обично 2-8 недеља)",
            taxation: "Пореске стопе се значајно разликују по јурисдикцијама",
            special: "Доступно на захтев са премијум ценама - Контактирајте нас за детаљну консултацију и прилагођену понуду"
        }
    },
    
    zh: {
        slovenia: {
            name: "斯洛文尼亚",
            code: "SI",
            overview: "斯洛文尼亚为进入欧盟市场提供了优质通道，拥有商业友好的法规和具有竞争力的运营成本。",
            advantages: [
                "欧盟成员国优势",
                "商业友好环境",
                "低运营成本",
                "战略性中欧位置",
                "稳定的政治环境"
            ],
            requirements: [
                "最低股本：€7,500",
                "至少需要一名董事",
                "在斯洛文尼亚注册办公室",
                "大型公司需要监事会",
                "年度报告义务"
            ],
            timeframe: "平均成立时间2-3周",
            taxation: "企业税率：19%"
        },
        serbia: {
            name: "塞尔维亚",
            code: "RS",
            overview: "塞尔维亚为国际公司提供战略位置优势，具有竞争性成本和不断改善的商业环境。",
            advantages: [
                "战略巴尔干位置",
                "具有竞争力的运营成本",
                "增长的经济",
                "熟练的劳动力",
                "欧盟入盟谈判"
            ],
            requirements: [
                "最低股本：€1",
                "至少需要一名董事",
                "在塞尔维亚注册办公室",
                "商业注册证书",
                "需要税务登记"
            ],
            timeframe: "平均成立时间1-3周",
            taxation: "企业税率：15%"
        },
        worldwide: {
            name: "世界其他地区",
            code: "WW",
            overview: "我们在全球许多其他司法管辖区提供公司注册服务。每个司法管辖区都有针对特定商业需求量身定制的独特优势和要求。",
            advantages: [
                "全球司法管辖区覆盖",
                "定制解决方案",
                "专业本地知识",
                "合规管理",
                "持续支持服务"
            ],
            requirements: [
                "要求因司法管辖区而异",
                "需要详细咨询",
                "需要定制文档",
                "当地监管合规",
                "适用更高的服务费"
            ],
            timeframe: "因司法管辖区而异（通常2-8周）",
            taxation: "税率因司法管辖区而显著不同",
            special: "应要求提供，采用高级定价 - 请联系我们获取详细咨询和定制报价"
        }
    }
};

// Translation data from the original website
const translations = {
    en: {
        // Navigation
        'nav-home': 'Home',
        'nav-services': 'Services',
        'nav-pricing': 'Pricing',
        'nav-contact': 'Contact',
        
        // Hero Section
        'hero-title-main': 'Company registration services',
        'hero-title-highlight': 'to grow your',
        'hero-title-end': 'business',
        'hero-subtitle': 'Join hundreds of successful businesses that use our expert company registration services to create powerful legal entities, ensure compliance, and build a more profitable global presence.',
        'hero-cta': 'Start now',
        'hero-stats-companies': 'Companies formed',
        'hero-stats-satisfaction': 'Client satisfaction',
        
        // Services Section
        'services-title': 'Our Services',
        'services-subtitle': 'Comprehensive business formation and corporate services worldwide',
        'service-formation-title': 'Company Formation',
        'service-formation-desc': 'Complete company registration services in multiple jurisdictions with all required documentation.',
        'service-nominee-title': 'Nominee Services',
        'service-nominee-desc': 'Professional nominee director and shareholder services to protect your privacy.',
        'service-banking-title': 'Bank Account Opening',
        'service-banking-desc': 'Assistance with opening corporate bank accounts in your chosen jurisdiction.',
        'service-compliance-title': 'Compliance Services',
        'service-compliance-desc': 'Ongoing compliance and annual filing services to keep your company in good standing.',
        'service-legal-title': 'Legal Documentation',
        'service-legal-desc': 'Apostilled documents, certificates, and legal documentation services.',
        'service-support-title': '24/7 Support',
        'service-support-desc': 'Round-the-clock customer support from our experienced corporate specialists.',
        
        // Noctrify Promo
        'promo-title': 'Great! Your consultation is booked. What about a website?',
        'promo-description': 'Since you\'re setting up a company, you\'ll likely need a professional website too! Our partner Noctrify specializes in creating stunning websites for new businesses.',
        'promo-feature-1': 'Professional web design',
        'promo-feature-2': 'Business-ready templates',
        'promo-feature-3': 'Complete online presence',
        'promo-visit-noctrify': 'Visit Noctrify',
        'promo-maybe-later': 'Maybe Later',
        
        // Countries Section
        'countries-title': 'Jurisdictions We Serve',
        'countries-subtitle': 'Choose from our carefully selected jurisdictions based on your business needs',
        
        // Country Cards
        'country-italy-name': 'Italy',
        'country-italy-desc': 'EU market access, strong legal framework',
        'country-slovenia-name': 'Slovenia',
        'country-slovenia-desc': 'EU member, business-friendly environment',
        'country-macedonia-name': 'North Macedonia',
        'country-macedonia-desc': 'Growing economy, low corporate tax',
        'country-serbia-name': 'Serbia',
        'country-serbia-desc': 'Strategic location, competitive costs',
        'country-bosnia-name': 'Bosnia & Herzegovina',
        'country-bosnia-desc': 'Emerging market opportunities',
        'country-bulgaria-name': 'Bulgaria',
        'country-bulgaria-desc': 'Growing economy, low corporate tax',
        'country-croatia-name': 'Croatia',
        'country-croatia-desc': 'EU member, tourism & tech hub',
        'country-usa-name': 'United States',
        'country-usa-desc': 'World\'s largest economy, Delaware & Wyoming',
        'country-uk-name': 'United Kingdom',
        'country-uk-desc': 'Global financial center, strong reputation',
        'country-greece-name': 'Greece',
        'country-greece-desc': 'EU member, strategic Mediterranean location',
        'country-uae-name': 'UAE (Dubai)',
        'country-uae-desc': 'Tax-free zones, gateway to Middle East',
        'country-singapore-name': 'Singapore',
        'country-singapore-desc': 'Asia-Pacific hub, excellent infrastructure',
        'country-cyprus-name': 'Cyprus',
        'country-cyprus-desc': 'EU member, attractive tax regime',
        'country-montenegro-name': 'Montenegro',
        'country-montenegro-desc': 'EU candidate, business opportunities',
        'country-worldwide-name': 'Rest of the World',
        'country-worldwide-desc': 'Other jurisdictions available on request',
        
        // Modal
        'modal-book-consultation': 'Book Consultation',
        'modal-overview': 'Overview',
        'modal-advantages': 'Key Advantages',
        'modal-requirements': 'Requirements',
        'modal-formation-details': 'Formation Details',
        'modal-timeframe': 'Timeframe',
        'modal-taxation': 'Taxation',
        'modal-special-notice': 'Special Notice',
        
        // Pricing Section
        'pricing-title': 'Transparent Pricing',
        'pricing-subtitle-alt': 'Choose the package that best fits your business needs',
        'pricing-europe-title': 'Europe Basic',
        'pricing-europe-desc': 'Perfect for EU market entry',
        'pricing-offshore-title': 'Offshore Basic',
        'pricing-offshore-desc': 'Privacy & tax optimization',
        'pricing-global-title': 'Global Hub Basic',
        'pricing-global-desc': 'Strategic global locations',
        'pricing-most-popular': 'Most Popular',
        
        // Features
        'feature-company-registration': 'Company registration',
        'feature-registered-address': 'Registered address',
        'feature-basic-documentation': 'Basic documentation',
        'feature-government-fees': 'Government fees included',
        'feature-formation-time-2-4': '2-4 weeks formation',
        'feature-nominee-services': 'Basic nominee services',
        'feature-formation-time-1-3': '1-3 weeks formation',
        'feature-banking-assistance': 'Basic banking assistance',
        'feature-formation-time-3-5': '3-5 weeks formation',
        'btn-get-started': 'Get Started',
        
        // Advanced Features
        'advanced-features-title': 'Need Advanced Features?',
        'advanced-features-desc': 'Bank account opening, tax optimization, nominee services, or complex structures?',
        'advanced-book-consultation': 'Book Consultation',
        
        // Contact Section
        'contact-ready-title': 'Ready to Start Your Business?',
        'contact-ready-subtitle': 'Book your free consultation and get a custom proposal within 24 hours',
        'schedule-consultation-title': 'Schedule Your Free Consultation',
        'schedule-consultation-subtitle': 'Choose a convenient time for your 30-minute strategy session',
        'consultant-expert-title': 'Business Formation Expert',
        'consultant-welcome-text': 'Welcome to our booking page. Please follow the instructions for adding events to our calendar.',
        'consultation-duration': '30 min call',
        'meeting-duration-title': '30-minute meeting',
        'meeting-type-desc': 'Video call or phone consultation',
        
        // Consultation Info
        'consultation-what-to-expect': 'What to Expect',
        'consultation-business-analysis': 'Business analysis and requirements review',
        'consultation-jurisdiction-recommendations': 'Jurisdiction recommendations and strategy',
        'consultation-custom-proposal': 'Custom proposal with timeline and pricing',
        'consultation-next-steps': 'Next steps and implementation plan',
        'consultation-come-prepared': 'Come Prepared',
        'consultation-business-goals': 'Your business goals and target audience',
        'consultation-preferred-jurisdiction': 'Preferred jurisdiction and reasoning',
        'consultation-required-features': 'Required features and functionality',
        'consultation-timeline-budget': 'Timeline and budget expectations',
        'consultation-guarantee': '100% FREE • NO COMMITMENTS',
        'consultation-alternative-contact': 'Alternative Contact Methods',
        'consultation-email-us': 'Email Us',
        'consultation-direct-call': 'Direct Call',
        
        // Trust Indicators
        'trust-response-time': '24h Response Time',
        'trust-response-desc': 'Quick turnaround guaranteed',
        'trust-no-pressure': 'No Pressure Consultation',
        'trust-no-pressure-desc': 'Honest advice, no sales pressure',
        'trust-professional': 'Professional Service',
        'trust-professional-desc': 'Expert guidance every step',
        'trust-confidential': 'Confidential Discussion',
        'trust-confidential-desc': 'Your information stays secure',
        
        // Footer
        'footer-privacy': 'Privacy Policy',
        'footer-copyright': '© 2025 Out of Box. All rights reserved.',
        
        // Privacy Policy
        'privacy-policy-title': 'Privacy Policy',
        'privacy-last-updated': 'Last updated: July 2025',
        'privacy-intro-title': '1. Introduction',
        'privacy-intro-text': 'At Out of Box, we respect your privacy. This simple privacy policy explains what information we collect when you visit our website and contact us about our company formation services.',
        'privacy-collect-title': '2. Information We Collect',
        'privacy-collect-text': 'We only collect information that you voluntarily provide to us:',
        'privacy-collect-contact': 'Contact Information: Your name, email address, and phone number when you fill out our contact form or book a consultation',
        'privacy-collect-business': 'Business Details: Information about your company formation needs and requirements',
        'privacy-collect-analytics': 'Website Analytics: Basic visitor statistics (like page views) through standard web analytics',
        'privacy-use-title': '3. How We Use Your Information',
        'privacy-use-text': 'We use your information simply to:',
        'privacy-use-respond': 'Respond to your inquiries about our company formation services',
        'privacy-use-quotes': 'Provide quotes and service proposals',
        'privacy-use-communicate': 'Communicate about ongoing company formation projects',
        'privacy-use-improve': 'Improve our website and services',
        'privacy-sharing-title': '4. Information Sharing',
        'privacy-sharing-text': 'We don\'t sell, rent, or share your personal information with anyone. Your contact details stay with us and are only used to respond to your business inquiries about company formation services.',
        'privacy-security-title': '5. Data Security',
        'privacy-security-text': 'We take reasonable steps to protect your information from unauthorized access. However, no internet transmission is completely secure, so we can\'t guarantee absolute security.',
        'privacy-cookies-title': '6. Cookies',
        'privacy-cookies-text': 'Our website may use basic cookies to improve your browsing experience and gather anonymous usage statistics. You can disable cookies in your browser settings if you prefer.',
        'privacy-rights-title': '7. Your Rights',
        'privacy-rights-text': 'You can:',
        'privacy-rights-know': 'Request to know what information we have about you',
        'privacy-rights-update': 'Ask us to update or delete your information',
        'privacy-rights-contact': 'Contact us anytime with privacy questions',
        'privacy-updates-title': '8. Updates',
        'privacy-updates-text': 'We may update this policy occasionally. Any changes will be posted on this page with a new "last updated" date.',
        'privacy-contact-title': '9. Contact Us',
        'privacy-contact-text': 'Questions about this privacy policy? Email us directly.',
        'privacy-contact-email': 'Email: info.itb@itb-solution.com',
        
        // Translation disclaimer
        'translation-disclaimer': 'Translations may not be 100% accurate'
    },
    
    de: {
        // Navigation
        'nav-home': 'Startseite',
        'nav-services': 'Dienstleistungen',
        'nav-pricing': 'Preise',
        'nav-contact': 'Kontakt',
        
        // Hero Section
        'hero-title-main': 'Unternehmensregistrierungsdienste',
        'hero-title-highlight': 'für das Wachstum Ihres',
        'hero-title-end': 'Unternehmens',
        'hero-subtitle': 'Schließen Sie sich Hunderten erfolgreicher Unternehmen an, die unsere professionellen Unternehmensregistrierungsdienste nutzen, um starke juristische Personen zu schaffen, Compliance zu gewährleisten und eine profitablere globale Präsenz aufzubauen.',
        'hero-cta': 'Jetzt starten',
        'hero-stats-companies': 'Gegründete Unternehmen',
        'hero-stats-satisfaction': 'Kundenzufriedenheit',
        
        // Services Section
        'services-title': 'Unsere Dienstleistungen',
        'services-subtitle': 'Umfassende Unternehmensregistrierung und Firmendienstleistungen weltweit',
        'service-formation-title': 'Unternehmensregistrierung',
        'service-formation-desc': 'Vollständige Unternehmensregistrierungsdienste in mehreren Jurisdiktionen mit allen erforderlichen Dokumenten.',
        'service-nominee-title': 'Nominee-Dienstleistungen',
        'service-nominee-desc': 'Professionelle Nominee-Direktor- und Aktionärsdienste zum Schutz Ihrer Privatsphäre.',
        'service-banking-title': 'Bankkonto-Eröffnung',
        'service-banking-desc': 'Unterstützung bei der Eröffnung von Firmenkonten in Ihrer gewählten Jurisdiktion.',
        'service-compliance-title': 'Compliance-Dienstleistungen',
        'service-compliance-desc': 'Laufende Compliance- und jährliche Einreichungsdienste, um Ihr Unternehmen in gutem Zustand zu halten.',
        'service-legal-title': 'Rechtsdokumentation',
        'service-legal-desc': 'Apostillierte Dokumente, Zertifikate und rechtliche Dokumentationsdienste.',
        'service-support-title': '24/7 Support',
        'service-support-desc': 'Rund-um-die-Uhr-Kundensupport von unseren erfahrenen Firmenspezialisten.',
        
        // Noctrify Promo
        'promo-title': 'Großartig! Ihre Beratung ist gebucht. Wie wäre es mit einer Website?',
        'promo-description': 'Da Sie ein Unternehmen gründen, benötigen Sie wahrscheinlich auch eine professionelle Website! Unser Partner Noctrify spezialisiert sich auf die Erstellung beeindruckender Websites für neue Unternehmen.',
        'promo-feature-1': 'Professionelles Webdesign',
        'promo-feature-2': 'Business-ready Vorlagen',
        'promo-feature-3': 'Komplette Online-Präsenz',
        'promo-visit-noctrify': 'Besuchen Sie Noctrify',
        'promo-maybe-later': 'Vielleicht später',
        
        // Countries Section
        'countries-title': 'Jurisdiktionen, die wir bedienen',
        'countries-subtitle': 'Wählen Sie aus unseren sorgfältig ausgewählten Jurisdiktionen basierend auf Ihren Geschäftsbedürfnissen',
        
        // Country Cards
        'country-italy-name': 'Italien',
        'country-italy-desc': 'EU-Marktzugang, starker rechtlicher Rahmen',
        'country-slovenia-name': 'Slowenien',
        'country-slovenia-desc': 'EU-Mitglied, geschäftsfreundliche Umgebung',
        'country-macedonia-name': 'Nordmazedonien',
        'country-macedonia-desc': 'Wachsende Wirtschaft, niedrige Körperschaftssteuer',
        'country-serbia-name': 'Serbien',
        'country-serbia-desc': 'Strategische Lage, wettbewerbsfähige Kosten',
        'country-bosnia-name': 'Bosnien und Herzegowina',
        'country-bosnia-desc': 'Chancen in aufstrebenden Märkten',
        'country-bulgaria-name': 'Bulgarien',
        'country-bulgaria-desc': 'Wachsende Wirtschaft, niedrige Körperschaftssteuer',
        'country-croatia-name': 'Kroatien',
        'country-croatia-desc': 'EU-Mitglied, Tourismus- und Tech-Hub',
        'country-usa-name': 'Vereinigte Staaten',
        'country-usa-desc': 'Weltgrößte Wirtschaft, Delaware & Wyoming',
        'country-uk-name': 'Vereinigtes Königreich',
        'country-uk-desc': 'Globales Finanzzentrum, starke Reputation',
        'country-greece-name': 'Griechenland',
        'country-greece-desc': 'EU-Mitglied, strategische Mittelmeer-Lage',
        'country-uae-name': 'VAE (Dubai)',
        'country-uae-desc': 'Steuerfreie Zonen, Tor zum Nahen Osten',
        'country-singapore-name': 'Singapur',
        'country-singapore-desc': 'Asien-Pazifik-Hub, exzellente Infrastruktur',
        'country-cyprus-name': 'Zypern',
        'country-cyprus-desc': 'EU-Mitglied, attraktives Steuerregime',
        'country-montenegro-name': 'Montenegro',
        'country-montenegro-desc': 'EU-Kandidat, Geschäftsmöglichkeiten',
        'country-worldwide-name': 'Rest der Welt',
        'country-worldwide-desc': 'Andere Jurisdiktionen auf Anfrage verfügbar',
        
        // Modal
        'modal-book-consultation': 'Beratung buchen',
        'modal-overview': 'Übersicht',
        'modal-advantages': 'Hauptvorteile',
        'modal-requirements': 'Anforderungen',
        'modal-formation-details': 'Gründungsdetails',
        'modal-timeframe': 'Zeitrahmen',
        'modal-taxation': 'Besteuerung',
        'modal-special-notice': 'Besonderer Hinweis',
        
        // Pricing Section
        'pricing-title': 'Transparente Preise',
        'pricing-subtitle-alt': 'Wählen Sie das Paket, das am besten zu Ihren Geschäftsbedürfnissen passt',
        'pricing-europe-title': 'Europa Basic',
        'pricing-europe-desc': 'Perfekt für den EU-Markteintritt',
        'pricing-offshore-title': 'Offshore Basic',
        'pricing-offshore-desc': 'Privatsphäre & Steueroptimierung',
        'pricing-global-title': 'Global Hub Basic',
        'pricing-global-desc': 'Strategische globale Standorte',
        'pricing-most-popular': 'Beliebteste',
        
        // Features
        'feature-company-registration': 'Unternehmensregistrierung',
        'feature-registered-address': 'Registrierte Adresse',
        'feature-basic-documentation': 'Grunddokumentation',
        'feature-government-fees': 'Regierungsgebühren enthalten',
        'feature-formation-time-2-4': '2-4 Wochen Gründung',
        'feature-nominee-services': 'Basis-Nominee-Dienste',
        'feature-formation-time-1-3': '1-3 Wochen Gründung',
        'feature-banking-assistance': 'Basis-Banking-Unterstützung',
        'feature-formation-time-3-5': '3-5 Wochen Gründung',
        'btn-get-started': 'Loslegen',
        
        // Advanced Features
        'advanced-features-title': 'Benötigen Sie erweiterte Funktionen?',
        'advanced-features-desc': 'Bankkontoeröffnung, Steueroptimierung, Nominee-Dienste oder komplexe Strukturen?',
        'advanced-book-consultation': 'Beratung buchen',
        
        // Contact Section
        'contact-ready-title': 'Bereit, Ihr Unternehmen zu gründen?',
        'contact-ready-subtitle': 'Buchen Sie Ihre kostenlose Beratung und erhalten Sie innerhalb von 24 Stunden ein individuelles Angebot',
        'schedule-consultation-title': 'Planen Sie Ihre kostenlose Beratung',
        'schedule-consultation-subtitle': 'Wählen Sie eine passende Zeit für Ihre 30-minütige Strategiesitzung',
        'consultant-expert-title': 'Experte für Unternehmensregistrierung',
        'consultant-welcome-text': 'Willkommen auf unserer Buchungsseite. Bitte folgen Sie den Anweisungen zum Hinzufügen von Terminen zu unserem Kalender.',
        'consultation-duration': '30 Min Anruf',
        'meeting-duration-title': '30-minütiges Meeting',
        'meeting-type-desc': 'Videoanruf oder Telefonberatung',
        
        // Consultation Info
        'consultation-what-to-expect': 'Was Sie erwartet',
        'consultation-business-analysis': 'Geschäftsanalyse und Anforderungsüberprüfung',
        'consultation-jurisdiction-recommendations': 'Jurisdiktionsempfehlungen und Strategie',
        'consultation-custom-proposal': 'Individuelles Angebot mit Zeitplan und Preisen',
        'consultation-next-steps': 'Nächste Schritte und Umsetzungsplan',
        'consultation-come-prepared': 'Vorbereitung',
        'consultation-business-goals': 'Ihre Geschäftsziele und Zielgruppe',
        'consultation-preferred-jurisdiction': 'Bevorzugte Jurisdiktion und Begründung',
        'consultation-required-features': 'Erforderliche Funktionen und Funktionalität',
        'consultation-timeline-budget': 'Zeitplan und Budgeterwartungen',
        'consultation-guarantee': '100% KOSTENLOS • KEINE VERPFLICHTUNGEN',
        'consultation-alternative-contact': 'Alternative Kontaktmethoden',
        'consultation-email-us': 'E-Mail senden',
        'consultation-direct-call': 'Direkter Anruf',
        
        // Trust Indicators
        'trust-response-time': '24h Antwortzeit',
        'trust-response-desc': 'Schnelle Bearbeitung garantiert',
        'trust-no-pressure': 'Druckfreie Beratung',
        'trust-no-pressure-desc': 'Ehrliche Beratung, kein Verkaufsdruck',
        'trust-professional': 'Professioneller Service',
        'trust-professional-desc': 'Expertenberatung bei jedem Schritt',
        'trust-confidential': 'Vertrauliche Diskussion',
        'trust-confidential-desc': 'Ihre Informationen bleiben sicher',
        
        // Footer
        'footer-privacy': 'Datenschutzrichtlinie',
        'footer-copyright': '© 2025 Out of Box. Alle Rechte vorbehalten.',
        
        // Privacy Policy
        'privacy-policy-title': 'Datenschutzrichtlinie',
        'privacy-last-updated': 'Zuletzt aktualisiert: Juli 2025',
        'privacy-intro-title': '1. Einführung',
        'privacy-intro-text': 'Bei Out of Box respektieren wir Ihre Privatsphäre. Diese einfache Datenschutzrichtlinie erklärt, welche Informationen wir sammeln, wenn Sie unsere Website besuchen und uns bezüglich unserer Unternehmensregistrierungsdienste kontaktieren.',
        'privacy-collect-title': '2. Informationen, die wir sammeln',
        'privacy-collect-text': 'Wir sammeln nur Informationen, die Sie uns freiwillig zur Verfügung stellen:',
        'privacy-collect-contact': 'Kontaktinformationen: Ihr Name, E-Mail-Adresse und Telefonnummer, wenn Sie unser Kontaktformular ausfüllen oder eine Beratung buchen',
        'privacy-collect-business': 'Geschäftsdetails: Informationen über Ihre Unternehmensregistrierungsbedürfnisse und -anforderungen',
        'privacy-collect-analytics': 'Website-Analytik: Grundlegende Besucherstatistiken (wie Seitenaufrufe) durch Standard-Web-Analytik',
        'privacy-use-title': '3. Wie wir Ihre Informationen verwenden',
        'privacy-use-text': 'Wir verwenden Ihre Informationen einfach, um:',
        'privacy-use-respond': 'Auf Ihre Anfragen bezüglich unserer Unternehmensregistrierungsdienste zu antworten',
        'privacy-use-quotes': 'Angebote und Serviceanträge bereitzustellen',
        'privacy-use-communicate': 'Über laufende Unternehmensregistrierungsprojekte zu kommunizieren',
        'privacy-use-improve': 'Unsere Website und Dienste zu verbessern',
        'privacy-sharing-title': '4. Informationsaustausch',
        'privacy-sharing-text': 'Wir verkaufen, vermieten oder teilen Ihre persönlichen Informationen nicht mit anderen. Ihre Kontaktdaten bleiben bei uns und werden nur verwendet, um auf Ihre Geschäftsanfragen zu Unternehmensregistrierungsdiensten zu antworten.',
        'privacy-security-title': '5. Datensicherheit',
        'privacy-security-text': 'Wir unternehmen angemessene Schritte, um Ihre Informationen vor unbefugtem Zugriff zu schützen. Allerdings ist keine Internetübertragung vollständig sicher, daher können wir keine absolute Sicherheit garantieren.',
        'privacy-cookies-title': '6. Cookies',
        'privacy-cookies-text': 'Unsere Website kann grundlegende Cookies verwenden, um Ihr Browsing-Erlebnis zu verbessern und anonyme Nutzungsstatistiken zu sammeln. Sie können Cookies in Ihren Browsereinstellungen deaktivieren, wenn Sie möchten.',
        'privacy-rights-title': '7. Ihre Rechte',
        'privacy-rights-text': 'Sie können:',
        'privacy-rights-know': 'Anfordern zu wissen, welche Informationen wir über Sie haben',
        'privacy-rights-update': 'Uns bitten, Ihre Informationen zu aktualisieren oder zu löschen',
        'privacy-rights-contact': 'Uns jederzeit mit Datenschutzfragen kontaktieren',
        'privacy-updates-title': '8. Updates',
        'privacy-updates-text': 'Wir können diese Richtlinie gelegentlich aktualisieren. Alle Änderungen werden auf dieser Seite mit einem neuen "zuletzt aktualisiert" Datum veröffentlicht.',
        'privacy-contact-title': '9. Kontaktieren Sie uns',
        'privacy-contact-text': 'Fragen zu dieser Datenschutzrichtlinie? E-Mail direkt an uns.',
        'privacy-contact-email': 'E-Mail: info.itb@itb-solution.com',
        
        // Translation disclaimer
        'translation-disclaimer': 'Übersetzungen sind möglicherweise nicht 100% genau'
    },
    
    ru: {
        // Navigation
        'nav-home': 'Главная',
        'nav-services': 'Услуги',
        'nav-pricing': 'Цены',
        'nav-contact': 'Контакты',
        
        // Hero Section
        'hero-title-main': 'Услуги регистрации компаний',
        'hero-title-highlight': 'для развития вашего',
        'hero-title-end': 'бизнеса',
        'hero-subtitle': 'Присоединяйтесь к сотням успешных компаний, которые используют наши экспертные услуги регистрации компаний для создания мощных юридических лиц, обеспечения соответствия и построения более прибыльного глобального присутствия.',
        'hero-cta': 'Начать сейчас',
        'hero-stats-companies': 'Компаний зарегистрировано',
        'hero-stats-satisfaction': 'Удовлетворенность клиентов',
        
        // Services Section
        'services-title': 'Наши услуги',
        'services-subtitle': 'Комплексные услуги по регистрации бизнеса и корпоративные услуги по всему миру',
        'service-formation-title': 'Регистрация компании',
        'service-formation-desc': 'Полные услуги регистрации компании в различных юрисдикциях со всей необходимой документацией.',
        'service-nominee-title': 'Номинальные услуги',
        'service-nominee-desc': 'Профессиональные услуги номинального директора и акционера для защиты вашей конфиденциальности.',
        'service-banking-title': 'Открытие банковского счета',
        'service-banking-desc': 'Помощь в открытии корпоративных банковских счетов в выбранной вами юрисдикции.',
        'service-compliance-title': 'Услуги соответствия',
        'service-compliance-desc': 'Постоянные услуги соответствия и ежегодной подачи документов для поддержания компании в хорошем состоянии.',
        'service-legal-title': 'Юридическая документация',
        'service-legal-desc': 'Апостилированные документы, сертификаты и услуги юридической документации.',
        'service-support-title': 'Поддержка 24/7',
        'service-support-desc': 'Круглосуточная поддержка клиентов от наших опытных корпоративных специалистов.',
        
        // Noctrify Promo
        'promo-title': 'Отлично! Ваша консультация забронирована. Как насчет веб-сайта?',
        'promo-description': 'Поскольку вы создаете компанию, вам, вероятно, также понадобится профессиональный веб-сайт! Наш партнер Noctrify специализируется на создании потрясающих веб-сайтов для новых предприятий.',
        'promo-feature-1': 'Профессиональный веб-дизайн',
        'promo-feature-2': 'Готовые для бизнеса шаблоны',
        'promo-feature-3': 'Полное онлайн-присутствие',
        'promo-visit-noctrify': 'Посетить Noctrify',
        'promo-maybe-later': 'Возможно, позже',
        
        // Countries Section
        'countries-title': 'Юрисдикции, которые мы обслуживаем',
        'countries-subtitle': 'Выберите из наших тщательно отобранных юрисдикций в зависимости от ваших бизнес-потребностей',
        
        // Country Cards
        'country-italy-name': 'Италия',
        'country-italy-desc': 'Доступ к рынку ЕС, сильная правовая база',
        'country-slovenia-name': 'Словения',
        'country-slovenia-desc': 'Член ЕС, дружественная к бизнесу среда',
        'country-macedonia-name': 'Северная Македония',
        'country-macedonia-desc': 'Растущая экономика, низкий корпоративный налог',
        'country-serbia-name': 'Сербия',
        'country-serbia-desc': 'Стратегическое расположение, конкурентные расходы',
        'country-bosnia-name': 'Босния и Герцеговина',
        'country-bosnia-desc': 'Возможности развивающегося рынка',
        'country-bulgaria-name': 'Болгария',
        'country-bulgaria-desc': 'Растущая экономика, низкий корпоративный налог',
        'country-croatia-name': 'Хорватия',
        'country-croatia-desc': 'Член ЕС, туристический и технологический центр',
        'country-usa-name': 'Соединенные Штаты',
        'country-usa-desc': 'Крупнейшая экономика мира, Делавэр и Вайоминг',
        'country-uk-name': 'Великобритания',
        'country-uk-desc': 'Глобальный финансовый центр, сильная репутация',
        'country-greece-name': 'Греция',
        'country-greece-desc': 'Член ЕС, стратегическое средиземноморское расположение',
        'country-uae-name': 'ОАЭ (Дубай)',
        'country-uae-desc': 'Безналоговые зоны, ворота на Ближний Восток',
        'country-singapore-name': 'Сингапур',
        'country-singapore-desc': 'Азиатско-Тихоокеанский центр, отличная инфраструктура',
        'country-cyprus-name': 'Кипр',
        'country-cyprus-desc': 'Член ЕС, привлекательный налоговый режим',
        'country-montenegro-name': 'Черногория',
        'country-montenegro-desc': 'Кандидат в ЕС, бизнес-возможности',
        'country-worldwide-name': 'Остальной мир',
        'country-worldwide-desc': 'Другие юрисдикции доступны по запросу',
        
        // Modal
        'modal-book-consultation': 'Записаться на консультацию',
        'modal-overview': 'Обзор',
        'modal-advantages': 'Основные преимущества',
        'modal-requirements': 'Требования',
        'modal-formation-details': 'Детали формирования',
        'modal-timeframe': 'Сроки',
        'modal-taxation': 'Налогообложение',
        'modal-special-notice': 'Особое уведомление',
        
        // Pricing Section
        'pricing-title': 'Прозрачные цены',
        'pricing-subtitle-alt': 'Выберите пакет, который лучше всего соответствует потребностям вашего бизнеса',
        'pricing-europe-title': 'Европа Базовый',
        'pricing-europe-desc': 'Идеально для выхода на рынок ЕС',
        'pricing-offshore-title': 'Оффшор Базовый',
        'pricing-offshore-desc': 'Конфиденциальность и налоговая оптимизация',
        'pricing-global-title': 'Глобальный центр Базовый',
        'pricing-global-desc': 'Стратегические глобальные локации',
        'pricing-most-popular': 'Самый популярный',
        
        // Features
        'feature-company-registration': 'Регистрация компании',
        'feature-registered-address': 'Зарегистрированный адрес',
        'feature-basic-documentation': 'Базовая документация',
        'feature-government-fees': 'Государственные пошлины включены',
        'feature-formation-time-2-4': '2-4 недели регистрации',
        'feature-nominee-services': 'Базовые номинальные услуги',
        'feature-formation-time-1-3': '1-3 недели регистрации',
        'feature-banking-assistance': 'Базовая банковская помощь',
        'feature-formation-time-3-5': '3-5 недель регистрации',
        'btn-get-started': 'Начать',
        
        // Advanced Features
        'advanced-features-title': 'Нужны расширенные функции?',
        'advanced-features-desc': 'Открытие банковского счета, налоговая оптимизация, номинальные услуги или сложные структуры?',
        'advanced-book-consultation': 'Забронировать консультацию',
        
        // Contact Section
        'contact-ready-title': 'Готовы начать свой бизнес?',
        'contact-ready-subtitle': 'Забронируйте бесплатную консультацию и получите индивидуальное предложение в течение 24 часов',
        'schedule-consultation-title': 'Запланируйте бесплатную консультацию',
        'schedule-consultation-subtitle': 'Выберите удобное время для 30-минутной стратегической сессии',
        'consultant-expert-title': 'Эксперт по регистрации бизнеса',
        'consultant-welcome-text': 'Добро пожаловать на нашу страницу бронирования. Пожалуйста, следуйте инструкциям для добавления событий в наш календарь.',
        'consultation-duration': '30 мин звонок',
        'meeting-duration-title': '30-минутная встреча',
        'meeting-type-desc': 'Видеозвонок или телефонная консультация',
        
        // Consultation Info
        'consultation-what-to-expect': 'Что ожидать',
        'consultation-business-analysis': 'Анализ бизнеса и обзор требований',
        'consultation-jurisdiction-recommendations': 'Рекомендации по юрисдикции и стратегии',
        'consultation-custom-proposal': 'Индивидуальное предложение с временными рамками и ценами',
        'consultation-next-steps': 'Следующие шаги и план реализации',
        'consultation-come-prepared': 'Подготовьтесь',
        'consultation-business-goals': 'Ваши бизнес-цели и целевая аудитория',
        'consultation-preferred-jurisdiction': 'Предпочтительная юрисдикция и обоснование',
        'consultation-required-features': 'Необходимые функции и функциональность',
        'consultation-timeline-budget': 'Ожидания по срокам и бюджету',
        'consultation-guarantee': '100% БЕСПЛАТНО • БЕЗ ОБЯЗАТЕЛЬСТВ',
        'consultation-alternative-contact': 'Альтернативные способы связи',
        'consultation-email-us': 'Напишите нам',
        'consultation-direct-call': 'Прямой звонок',
        
        // Trust Indicators
        'trust-response-time': 'Время ответа 24ч',
        'trust-response-desc': 'Быстрая обработка гарантирована',
        'trust-no-pressure': 'Консультация без давления',
        'trust-no-pressure-desc': 'Честные советы, никакого давления продаж',
        'trust-professional': 'Профессиональный сервис',
        'trust-professional-desc': 'Экспертное руководство на каждом шагу',
        'trust-confidential': 'Конфиденциальное обсуждение',
        'trust-confidential-desc': 'Ваша информация остается в безопасности',
        
        // Footer
        'footer-privacy': 'Политика конфиденциальности',
        'footer-copyright': '© 2025 Out of Box. Все права защищены.',
        
        // Privacy Policy
        'privacy-policy-title': 'Политика конфиденциальности',
        'privacy-last-updated': 'Последнее обновление: июль 2025',
        'privacy-intro-title': '1. Введение',
        'privacy-intro-text': 'В Out of Box мы уважаем вашу конфиденциальность. Эта простая политика конфиденциальности объясняет, какую информацию мы собираем, когда вы посещаете наш веб-сайт и связываетесь с нами по поводу наших услуг регистрации компаний.',
        'privacy-collect-title': '2. Информация, которую мы собираем',
        'privacy-collect-text': 'Мы собираем только ту информацию, которую вы добровольно предоставляете нам:',
        'privacy-collect-contact': 'Контактная информация: ваше имя, адрес электронной почты и номер телефона, когда вы заполняете нашу контактную форму или бронируете консультацию',
        'privacy-collect-business': 'Деловые детали: информация о ваших потребностях и требованиях к регистрации компании',
        'privacy-collect-analytics': 'Веб-аналитика: базовая статистика посетителей (например, просмотры страниц) через стандартную веб-аналитику',
        'privacy-use-title': '3. Как мы используем вашу информацию',
        'privacy-use-text': 'Мы используем вашу информацию просто для:',
        'privacy-use-respond': 'Ответа на ваши запросы о наших услугах регистрации компаний',
        'privacy-use-quotes': 'Предоставления расценок и предложений услуг',
        'privacy-use-communicate': 'Общения по текущим проектам регистрации компаний',
        'privacy-use-improve': 'Улучшения нашего веб-сайта и услуг',
        'privacy-sharing-title': '4. Обмен информацией',
        'privacy-sharing-text': 'Мы не продаем, не арендуем и не делимся вашей личной информацией ни с кем. Ваши контактные данные остаются у нас и используются только для ответа на ваши деловые запросы об услугах регистрации компаний.',
        'privacy-security-title': '5. Безопасность данных',
        'privacy-security-text': 'Мы принимаем разумные меры для защиты вашей информации от несанкционированного доступа. Однако ни одна интернет-передача не является полностью безопасной, поэтому мы не можем гарантировать абсолютную безопасность.',
        'privacy-cookies-title': '6. Файлы cookie',
        'privacy-cookies-text': 'Наш веб-сайт может использовать базовые файлы cookie для улучшения вашего просмотра и сбора анонимной статистики использования. Вы можете отключить файлы cookie в настройках браузера, если хотите.',
        'privacy-rights-title': '7. Ваши права',
        'privacy-rights-text': 'Вы можете:',
        'privacy-rights-know': 'Запросить информацию о том, какая информация у нас есть о вас',
        'privacy-rights-update': 'Попросить нас обновить или удалить вашу информацию',
        'privacy-rights-contact': 'Связаться с нами в любое время с вопросами о конфиденциальности',
        'privacy-updates-title': '8. Обновления',
        'privacy-updates-text': 'Мы можем время от времени обновлять эту политику. Все изменения будут размещены на этой странице с новой датой "последнего обновления".',
        'privacy-contact-title': '9. Свяжитесь с нами',
        'privacy-contact-text': 'Вопросы об этой политике конфиденциальности? Напишите нам напрямую.',
        'privacy-contact-email': 'Электронная почта: info.itb@itb-solution.com',
        
        // Translation disclaimer
        'translation-disclaimer': 'Переводы могут быть не на 100% точными'
    },
    
    sr: {
        // Navigation
        'nav-home': 'Почетна',
        'nav-services': 'Услуге',
        'nav-pricing': 'Цене',
        'nav-contact': 'Контакт',
        
        // Hero Section
        'hero-title-main': 'Услуге регистрације компанија',
        'hero-title-highlight': 'за раст вашег',
        'hero-title-end': 'бизниса',
        'hero-subtitle': 'Придружите се стотинама успешних предузећа која користе наше стручне услуге регистрације компанија за стварање моћних правних ентитета, обезбеђивање усклађености и изградњу профитабилније глобалне присутности.',
        'hero-cta': 'Почни сада',
        'hero-stats-companies': 'Формираних компанија',
        'hero-stats-satisfaction': 'Задовољство клијената',
        
        // Services Section
        'services-title': 'Наше услуге',
        'services-subtitle': 'Свеобухватне услуге формирања предузећа и корпоративне услуге широм света',
        'service-formation-title': 'Формирање компаније',
        'service-formation-desc': 'Комплетне услуге регистрације компаније у више јурисдикција са свом потребном документацијом.',
        'service-nominee-title': 'Nominee услуге',
        'service-nominee-desc': 'Професионалне nominee директорске и акционарске услуге за заштиту ваше приватности.',
        'service-banking-title': 'Отварање банковног рачуна',
        'service-banking-desc': 'Помоћ у отварању корпоративних банковних рачуна у вашој изабраној јурисдикцији.',
        'service-compliance-title': 'Услуге усклађености',
        'service-compliance-desc': 'Стални сервиси усклађености и годишњег подношења за одржавање ваше компаније у добром стању.',
        'service-legal-title': 'Правна документација',
        'service-legal-desc': 'Апостилирани документи, сертификати и услуге правне документације.',
        'service-support-title': '24/7 подршка',
        'service-support-desc': 'Непрекидна подршка клијентима од наших искусних корпоративних специјалиста.',
        
        // Countries Section
        'countries-title': 'Јурисдикције које служимо',
        'countries-subtitle': 'Изаберите из наших пажљиво одабраних јурисдикција на основу ваших пословних потреба',
        
        // Country Cards
        'country-italy-name': 'Италија',
        'country-italy-desc': 'Приступ ЕУ тржишту, јак правни оквир',
        'country-slovenia-name': 'Словенија',
        'country-slovenia-desc': 'Чланица ЕУ, пословно пријатељско окружење',
        'country-macedonia-name': 'Северна Македонија',
        'country-macedonia-desc': 'Растућа економија, низак корпоративни порез',
        'country-serbia-name': 'Србија',
        'country-serbia-desc': 'Стратешка локација, конкурентни трошкови',
        'country-bosnia-name': 'Босна и Херцеговина',
        'country-bosnia-desc': 'Могућности тржишта у развоју',
        'country-bulgaria-name': 'Бугарска',
        'country-bulgaria-desc': 'Растућа економија, низак корпоративни порез',
        'country-croatia-name': 'Хрватска',
        'country-croatia-desc': 'Чланица ЕУ, туристички и технолошки центар',
        'country-usa-name': 'Сједињене Државе',
        'country-usa-desc': 'Највећа светска економија, Делавер и Вајоминг',
        'country-uk-name': 'Уједињено Краљевство',
        'country-uk-desc': 'Глобални финансијски центар, јака репутација',
        'country-greece-name': 'Грчка',
        'country-greece-desc': 'Чланица ЕУ, стратешка медитеранска локација',
        'country-uae-name': 'УАЕ (Дубаи)',
        'country-uae-desc': 'Зоне без пореза, капија ка Блиском истоку',
        'country-singapore-name': 'Сингапур',
        'country-singapore-desc': 'Азијско-пацифички центар, одлична инфраструктура',
        'country-cyprus-name': 'Кипар',
        'country-cyprus-desc': 'Чланица ЕУ, привлачан порески режим',
        'country-montenegro-name': 'Црна Гора',
        'country-montenegro-desc': 'Кандидат за ЕУ, пословне могућности',
        'country-worldwide-name': 'Остатак света',
        'country-worldwide-desc': 'Остале јурисдикције доступне на захтев',
        
        // Modal
        'modal-book-consultation': 'Закажите консултацију',
        'modal-overview': 'Преглед',
        'modal-advantages': 'Кључне предности',
        'modal-requirements': 'Захтеви',
        'modal-formation-details': 'Детаљи формирања',
        'modal-timeframe': 'Временски оквир',
        'modal-taxation': 'Опорезивање',
        'modal-special-notice': 'Посебно обавештење',
        
        // Pricing Section
        'pricing-title': 'Транспарентне цене',
        'pricing-subtitle-alt': 'Изаберите пакет који најбоље одговара потребама вашег пословања',
        'pricing-europe-title': 'Европа основно',
        'pricing-europe-desc': 'Савршено за улазак на ЕУ тржиште',
        'pricing-offshore-title': 'Офшор основно',
        'pricing-offshore-desc': 'Приватност и пореска оптимизација',
        'pricing-global-title': 'Глобални центар основно',
        'pricing-global-desc': 'Стратешке глобалне локације',
        'pricing-most-popular': 'Најпопуларније',
        
        // Features
        'feature-company-registration': 'Регистрација компаније',
        'feature-registered-address': 'Регистрована адреса',
        'feature-basic-documentation': 'Основна документација',
        'feature-government-fees': 'Владине таксе укључене',
        'feature-formation-time-2-4': '2-4 недеље формирања',
        'feature-nominee-services': 'Основне nominee услуге',
        'feature-formation-time-1-3': '1-3 недеље формирања',
        'feature-banking-assistance': 'Основна банкарска помоћ',
        'feature-formation-time-3-5': '3-5 недеља формирања',
        'btn-get-started': 'Почни',
        
        // Advanced Features
        'advanced-features-title': 'Потребне су вам напредне функције?',
        'advanced-features-desc': 'Отварање банковног рачуна, пореска оптимизација, nominee услуге или сложене структуре?',
        'advanced-book-consultation': 'Резервиши консултацију',
        
        // Contact Section
        'contact-ready-title': 'Спремни сте да покренете свој бизнис?',
        'contact-ready-subtitle': 'Резервишите бесплатну консултацију и добијте прилагођену понуду у року од 24 сата',
        'schedule-consultation-title': 'Закажите бесплатну консултацију',
        'schedule-consultation-subtitle': 'Изаберите погодно време за вашу 30-минутну стратешку сесију',
        'consultant-expert-title': 'Експерт за формирање бизниса',
        'consultant-welcome-text': 'Добродошли на нашу страницу за резервацију. Молимо пратите упутства за додавање догађаја у наш календар.',
        'consultation-duration': '30 мин позив',
        'meeting-duration-title': '30-минутни састанак',
        'meeting-type-desc': 'Видео позив или телефонска консултација',
        
        // Consultation Info
        'consultation-what-to-expect': 'Шта да очекујете',
        'consultation-business-analysis': 'Анализа пословања и преглед захтева',
        'consultation-jurisdiction-recommendations': 'Препоруке за јурисдикцију и стратегију',
        'consultation-custom-proposal': 'Прилагођена понуда са временским оквиром и ценама',
        'consultation-next-steps': 'Следећи кораци и план имплементације',
        'consultation-come-prepared': 'Дођите припремљени',
        'consultation-business-goals': 'Ваши пословни циљеви и циљна група',
        'consultation-preferred-jurisdiction': 'Жељена јурисдикција и образложење',
        'consultation-required-features': 'Потребне функције и функционалност',
        'consultation-timeline-budget': 'Очекивања времена и буџета',
        'consultation-guarantee': '100% БЕСПЛАТНО • БЕЗ ОБАВЕЗА',
        'consultation-alternative-contact': 'Алтернативни начини контакта',
        'consultation-email-us': 'Пошаљите емаил',
        'consultation-direct-call': 'Директан позив',
        
        // Trust Indicators
        'trust-response-time': 'Време одговора 24ч',
        'trust-response-desc': 'Брза обрада гарантована',
        'trust-no-pressure': 'Консултација без притиска',
        'trust-no-pressure-desc': 'Искрени савети, без притиска продаје',
        'trust-professional': 'Професионална услуга',
        'trust-professional-desc': 'Експертско вођење на сваком кораку',
        'trust-confidential': 'Поверљива дискусија',
        'trust-confidential-desc': 'Ваше информације остају сигурне',
        
        // Footer
        'footer-privacy': 'Политика приватности',
        'footer-copyright': '© 2025 Out of Box. Сва права задржана.',
        
        // Privacy Policy
        'privacy-policy-title': 'Политика приватности',
        'privacy-last-updated': 'Последње ажурирање: јул 2025',
        'privacy-intro-title': '1. Увод',
        'privacy-intro-text': 'У Out of Box поштујемо вашу приватност. Ова једноставна политика приватности објашњава које информације прикупљамо када посетите нашу веб-страницу и контактирате нас у вези са нашим услугама формирања компанија.',
        'privacy-collect-title': '2. Информације које прикупљамо',
        'privacy-collect-text': 'Прикупљамо само информације које нам добровољно пружате:',
        'privacy-collect-contact': 'Контакт информације: ваше име, е-мејл адресу и број телефона када попуните наш контакт формулар или закажете консултацију',
        'privacy-collect-business': 'Пословни детаљи: информације о вашим потребама и захтевима за формирање компаније',
        'privacy-collect-analytics': 'Веб аналитика: основну статистику посетилаца (попут прегледа страница) кроз стандардну веб аналитику',
        'privacy-use-title': '3. Како користимо ваше информације',
        'privacy-use-text': 'Користимо ваше информације једноставно да:',
        'privacy-use-respond': 'Одговоримо на ваше упите о нашим услугама формирања компанија',
        'privacy-use-quotes': 'Пружимо понуде и предлоге услуга',
        'privacy-use-communicate': 'Комуницирамо о текућим пројектима формирања компанија',
        'privacy-use-improve': 'Побољшамо нашу веб-страницу и услуге',
        'privacy-sharing-title': '4. Дељење информација',
        'privacy-sharing-text': 'Не продајемо, не изнајмљујемо нити делимо ваше личне информације ни са ким. Ваши контакт подаци остају код нас и користе се само за одговарање на ваше пословне упите о услугама формирања компанија.',
        'privacy-security-title': '5. Безбедност података',
        'privacy-security-text': 'Предузимамо разумне кораке да заштитимо ваше информације од неовлашћеног приступа. Међутим, ниједан интернет пренос није потпуно сигуран, па не можемо гарантовати апсолутну безбедност.',
        'privacy-cookies-title': '6. Колачићи',
        'privacy-cookies-text': 'Наша веб-страница може користити основне колачиће да побољша ваше искуство прегледања и прикупи анонимну статистику коришћења. Можете онемогућити колачиће у подешавањима прегледача ако желите.',
        'privacy-rights-title': '7. Ваша права',
        'privacy-rights-text': 'Можете:',
        'privacy-rights-know': 'Затражити да знате које информације имамо о вама',
        'privacy-rights-update': 'Затражити од нас да ажурирамо или обришемо ваше информације',
        'privacy-rights-contact': 'Контактирати нас у било које време са питањима о приватности',
        'privacy-updates-title': '8. Ажурирања',
        'privacy-updates-text': 'Можемо повремено ажурирати ову политику. Све измене ће бити објављене на овој страници са новим датумом "последњег ажурирања".',
        'privacy-contact-title': '9. Контактирајте нас',
        'privacy-contact-text': 'Питања о овој политици приватности? Пошаљите нам е-мејл директно.',
        'privacy-contact-email': 'Е-мејл: info.itb@itb-solution.com',
        
        // Translation disclaimer
        'translation-disclaimer': 'Преводи можда нису 100% тачни'
    },
    
    zh: {
        // Navigation
        'nav-home': '首页',
        'nav-services': '服务',
        'nav-pricing': '价格',
        'nav-contact': '联系',
        
        // Hero Section
        'hero-title-main': '公司注册服务',
        'hero-title-highlight': '助力您的',
        'hero-title-end': '业务发展',
        'hero-subtitle': '加入数百家成功企业，使用我们专业的公司注册服务创建强大的法律实体，确保合规性，建立更盈利的全球业务。',
        'hero-cta': '立即开始',
        'hero-stats-companies': '已注册公司',
        'hero-stats-satisfaction': '客户满意度',
        
        // Services Section
        'services-title': '我们的服务',
        'services-subtitle': '全球综合商业成立和企业服务',
        'service-formation-title': '公司注册',
        'service-formation-desc': '在多个司法管辖区提供完整的公司注册服务，包含所有必需文件。',
        'service-nominee-title': '代名人服务',
        'service-nominee-desc': '专业代名人董事和股东服务，保护您的隐私。',
        'service-banking-title': '银行开户',
        'service-banking-desc': '协助在您选择的司法管辖区开设企业银行账户。',
        'service-compliance-title': '合规服务',
        'service-compliance-desc': '持续的合规和年度申报服务，保持您的公司良好状态。',
        'service-legal-title': '法律文件',
        'service-legal-desc': '认证文件、证书和法律文档服务。',
        'service-support-title': '24/7 支持',
        'service-support-desc': '我们经验丰富的企业专家提供全天候客户支持。',
        
        // Countries Section
        'countries-title': '我们服务的司法管辖区',
        'countries-subtitle': '根据您的业务需求从我们精心选择的司法管辖区中选择',
        
        // Country Cards
        'country-italy-name': '意大利',
        'country-italy-desc': '欧盟市场准入，强大的法律框架',
        'country-slovenia-name': '斯洛文尼亚',
        'country-slovenia-desc': '欧盟成员，商业友好环境',
        'country-macedonia-name': '北马其顿',
        'country-macedonia-desc': '增长的经济，低企业税',
        'country-serbia-name': '塞尔维亚',
        'country-serbia-desc': '战略位置，竞争成本',
        'country-bosnia-name': '波斯尼亚和黑塞哥维那',
        'country-bosnia-desc': '新兴市场机会',
        'country-bulgaria-name': '保加利亚',
        'country-bulgaria-desc': '增长的经济，低企业税',
        'country-croatia-name': '克罗地亚',
        'country-croatia-desc': '欧盟成员，旅游和科技中心',
        'country-usa-name': '美国',
        'country-usa-desc': '世界最大经济体，特拉华州和怀俄明州',
        'country-uk-name': '英国',
        'country-uk-desc': '全球金融中心，强大声誉',
        'country-greece-name': '希腊',
        'country-greece-desc': '欧盟成员，战略地中海位置',
        'country-uae-name': '阿联酋（迪拜）',
        'country-uae-desc': '免税区，中东门户',
        'country-singapore-name': '新加坡',
        'country-singapore-desc': '亚太中心，优秀基础设施',
        'country-cyprus-name': '塞浦路斯',
        'country-cyprus-desc': '欧盟成员，有吸引力的税收制度',
        'country-montenegro-name': '黑山',
        'country-montenegro-desc': '欧盟候选国，商业机会',
        'country-worldwide-name': '世界其他地区',
        'country-worldwide-desc': '其他司法管辖区可应要求提供',
        
        // Modal
        'modal-book-consultation': '预约咨询',
        'modal-overview': '概述',
        'modal-advantages': '主要优势',
        'modal-requirements': '要求',
        'modal-formation-details': '成立详情',
        'modal-timeframe': '时间框架',
        'modal-taxation': '税收',
        'modal-special-notice': '特别通知',
        
        // Pricing Section
        'pricing-title': '透明定价',
        'pricing-subtitle-alt': '选择最适合您业务需求的套餐',
        'pricing-europe-title': '欧洲基础',
        'pricing-europe-desc': '进入欧盟市场的完美选择',
        'pricing-offshore-title': '离岸基础',
        'pricing-offshore-desc': '隐私和税务优化',
        'pricing-global-title': '全球中心基础',
        'pricing-global-desc': '战略全球位置',
        'pricing-most-popular': '最受欢迎',
        
        // Features
        'feature-company-registration': '公司注册',
        'feature-registered-address': '注册地址',
        'feature-basic-documentation': '基础文档',
        'feature-government-fees': '包含政府费用',
        'feature-formation-time-2-4': '2-4周注册',
        'feature-nominee-services': '基础代名人服务',
        'feature-formation-time-1-3': '1-3周注册',
        'feature-banking-assistance': '基础银行协助',
        'feature-formation-time-3-5': '3-5周注册',
        'btn-get-started': '开始',
        
        // Advanced Features
        'advanced-features-title': '需要高级功能？',
        'advanced-features-desc': '银行开户、税务优化、代名人服务或复杂结构？',
        'advanced-book-consultation': '预约咨询',
        
        // Contact Section
        'contact-ready-title': '准备开始您的业务？',
        'contact-ready-subtitle': '预约免费咨询，24小时内获得定制方案',
        'schedule-consultation-title': '安排您的免费咨询',
        'schedule-consultation-subtitle': '为您的30分钟策略会议选择合适的时间',
        'consultant-expert-title': '业务成立专家',
        'consultant-welcome-text': '欢迎来到我们的预约页面。请按照说明将事件添加到我们的日历中。',
        'consultation-duration': '30分钟通话',
        'meeting-duration-title': '30分钟会议',
        'meeting-type-desc': '视频通话或电话咨询',
        
        // Consultation Info
        'consultation-what-to-expect': '期待什么',
        'consultation-business-analysis': '业务分析和需求审查',
        'consultation-jurisdiction-recommendations': '司法管辖区建议和策略',
        'consultation-custom-proposal': '定制提案，包含时间表和价格',
        'consultation-next-steps': '后续步骤和实施计划',
        'consultation-come-prepared': '准备工作',
        'consultation-business-goals': '您的业务目标和目标受众',
        'consultation-preferred-jurisdiction': '首选司法管辖区及理由',
        'consultation-required-features': '所需功能和特性',
        'consultation-timeline-budget': '时间表和预算期望',
        'consultation-guarantee': '100% 免费 • 无义务',
        'consultation-alternative-contact': '其他联系方式',
        'consultation-email-us': '发送邮件',
        'consultation-direct-call': '直接通话',
        
        // Trust Indicators
        'trust-response-time': '24小时响应时间',
        'trust-response-desc': '保证快速处理',
        'trust-no-pressure': '无压力咨询',
        'trust-no-pressure-desc': '诚实建议，无销售压力',
        'trust-professional': '专业服务',
        'trust-professional-desc': '每一步的专家指导',
        'trust-confidential': '机密讨论',
        'trust-confidential-desc': '您的信息保持安全',
        
        // Footer
        'footer-privacy': '隐私政策',
        'footer-copyright': '© 2025 Out of Box. 保留所有权利。',
        
        // Privacy Policy
        'privacy-policy-title': '隐私政策',
        'privacy-last-updated': '最后更新：2025年7月',
        'privacy-intro-title': '1. 简介',
        'privacy-intro-text': '在Out of Box，我们尊重您的隐私。这个简单的隐私政策解释了当您访问我们的网站并就我们的公司注册服务联系我们时，我们收集的信息。',
        'privacy-collect-title': '2. 我们收集的信息',
        'privacy-collect-text': '我们只收集您主动提供给我们的信息：',
        'privacy-collect-contact': '联系信息：当您填写我们的联系表格或预约咨询时的姓名、电子邮件地址和电话号码',
        'privacy-collect-business': '业务详情：关于您的公司注册需求和要求的信息',
        'privacy-collect-analytics': '网站分析：通过标准网络分析的基本访客统计（如页面浏览量）',
        'privacy-use-title': '3. 我们如何使用您的信息',
        'privacy-use-text': '我们使用您的信息只是为了：',
        'privacy-use-respond': '回应您对我们公司注册服务的咨询',
        'privacy-use-quotes': '提供报价和服务建议',
        'privacy-use-communicate': '就正在进行的公司注册项目进行沟通',
        'privacy-use-improve': '改进我们的网站和服务',
        'privacy-sharing-title': '4. 信息分享',
        'privacy-sharing-text': '我们不会向任何人出售、出租或分享您的个人信息。您的联系方式只会保留在我们这里，仅用于回应您关于公司注册服务的业务咨询。',
        'privacy-security-title': '5. 数据安全',
        'privacy-security-text': '我们采取合理措施保护您的信息免受未经授权的访问。但是，没有任何互联网传输是完全安全的，因此我们无法保证绝对安全。',
        'privacy-cookies-title': '6. Cookie',
        'privacy-cookies-text': '我们的网站可能使用基本cookie来改善您的浏览体验并收集匿名使用统计。如果您愿意，可以在浏览器设置中禁用cookie。',
        'privacy-rights-title': '7. 您的权利',
        'privacy-rights-text': '您可以：',
        'privacy-rights-know': '要求了解我们拥有的关于您的信息',
        'privacy-rights-update': '要求我们更新或删除您的信息',
        'privacy-rights-contact': '随时就隐私问题联系我们',
        'privacy-updates-title': '8. 更新',
        'privacy-updates-text': '我们可能会偶尔更新此政策。任何更改都将在此页面上发布，并附上新的"最后更新"日期。',
        'privacy-contact-title': '9. 联系我们',
        'privacy-contact-text': '对此隐私政策有疑问？直接发邮件给我们。',
        'privacy-contact-email': '邮箱：info.itb@itb-solution.com',
        
        // Translation disclaimer
        'translation-disclaimer': '翻译可能不是100%准确'
    }
};

// Create translation context
const TranslationContext = createContext();

// Translation provider component
export const TranslationProvider = ({ children }) => {
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        // Try to get from localStorage, fallback to 'en'
        try {
            return localStorage.getItem('selectedLanguage') || 'en';
        } catch {
            return 'en';
        }
    });

    // Function to get translation
    const t = (key) => {
        return translations[currentLanguage]?.[key] || translations.en[key] || key;
    };

    // Function to get country data
    const getCountryData = (countryId) => {
        return countryData[currentLanguage]?.[countryId] || countryData.en[countryId] || null;
    };

    // Function to change language
    const changeLanguage = (lang) => {
        setCurrentLanguage(lang);
        try {
            localStorage.setItem('selectedLanguage', lang);
        } catch {
            // localStorage not available
        }
    };

    // Available languages
    const languages = [
        { code: 'en', name: 'English', flag: 'https://flagcdn.com/32x24/us.png' },
        { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/32x24/de.png' },
        { code: 'ru', name: 'Русский', flag: 'https://flagcdn.com/32x24/ru.png' },
        { code: 'sr', name: 'Српски', flag: 'https://flagcdn.com/32x24/rs.png' },
        { code: 'zh', name: '中文', flag: 'https://flagcdn.com/32x24/cn.png' }
    ];

    const value = {
        currentLanguage,
        changeLanguage,
        t,
        getCountryData,
        languages
    };

    return (
        <TranslationContext.Provider value={value}>
            {children}
        </TranslationContext.Provider>
    );
};

// Custom hook to use translation
export const useTranslation = () => {
    const context = useContext(TranslationContext);
    if (!context) {
        throw new Error('useTranslation must be used within a TranslationProvider');
    }
    return context;
};

export default TranslationContext; 